interface LoadingProps {
    variant?: "fullscreen" | "inline";
    message?: string;
    size?: number;
}

export default function Loading({
    variant = "fullscreen",
    message,
    size = 24,
}: LoadingProps) {
    /* ── Inline spinner ─────────────────────────────────────── */
    if (variant === "inline") {
        return (
            <span
                role="status"
                aria-label="Loading"
                style={{ width: size, height: size }}
                className="inline-flex items-center justify-center shrink-0">
                <svg
                    viewBox="0 0 36 36"
                    fill="none"
                    style={{ width: size, height: size }}
                    className="animate-spin">
                    {/* Track */}
                    <circle
                        cx="18"
                        cy="18"
                        r="14"
                        stroke="currentColor"
                        strokeWidth="3"
                        className="text-gold/20 dark:text-gold/10"
                    />
                    {/* Arc */}
                    <path
                        d="M18 4 a14 14 0 0 1 14 14"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        className="text-gold dark:text-gold-light"
                    />
                </svg>
            </span>
        );
    }

    /* ── Full-screen overlay ────────────────────────────────── */
    return (
        <div
            role="status"
            aria-label="Loading"
            className="fixed inset-0 z-9999 flex flex-col items-center justify-center
                       bg-cream/90 dark:bg-dark/90 backdrop-blur-md
                       transition-colors duration-300">
            {/* Rings container */}
            <div className="relative flex items-center justify-center mb-12 select-none">
                {/* Outer slow ring */}
                <span
                    className="absolute rounded-full border-2 border-transparent
                               border-t-gold/40 border-r-gold/20
                               dark:border-t-gold-light/40 dark:border-r-gold-light/20
                               animate-spin"
                    style={{
                        width: 96,
                        height: 96,
                        animationDuration: "2.4s",
                        animationTimingFunction: "linear",
                    }}
                />

                {/* Middle ring */}
                <span
                    className="absolute rounded-full border-2 border-transparent
                               border-b-gold/60 border-l-gold/30
                               dark:border-b-gold-light/60 dark:border-l-gold-light/30
                               animate-spin"
                    style={{
                        width: 72,
                        height: 72,
                        animationDuration: "1.6s",
                        animationTimingFunction: "linear",
                        animationDirection: "reverse",
                    }}
                />

                {/* Inner fast ring */}
                <span
                    className="absolute rounded-full border-[3px] border-transparent
                               border-t-gold border-r-gold/50
                               dark:border-t-gold-light dark:border-r-gold-light/50
                               animate-spin"
                    style={{
                        width: 48,
                        height: 48,
                        animationDuration: "0.9s",
                        animationTimingFunction: "linear",
                    }}
                />

                {/* Center diamond/logo mark */}
                <span
                    className="w-7 h-7 bg-gold/10 dark:bg-gold/15 rounded-md rotate-45
                               border border-gold/40 dark:border-gold-light/30
                               animate-pulse"
                />
            </div>

            {/* Wordmark */}
            <p
                className="font-serif text-2xl font-bold tracking-widest text-dark dark:text-white
                          animate-pulse select-none">
                OMAH
                <em className="not-italic text-gold dark:text-gold-light">·</em>
                RING
            </p>

            {/* Optional message */}
            {message && (
                <p className="mt-3 text-sm tracking-wide text-dark/50 dark:text-white/40 font-sans select-none">
                    {message}
                </p>
            )}
        </div>
    );
}
